import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import {
  isDefinedBoolean,
  isDefinedObject,
  isDefinedString,
  isPositiveNumber,
  isString,
  reduce
} from "et3-type-guards";
import {
  doc,
  getFirestore,
  onSnapshot,
  type DocumentData,
  type FirestoreError,
  type Unsubscribe
} from "firebase/firestore";

import i18n from "../../constants/i18n";
import { firebaseApp } from "../../services/firebase";
import configuration from "../../configuration/keys";

import getCorePersonalStore from "../core/core-personal-store";
import getCorePopUpStore from "../core/core-popup-store";
import getCoreStorageStore from "../core/core-storage-store";
import {
  E_LOCAL_STORAGE_KEY,
  E_STORAGE_TYPE
} from "../../constants/storage-constants";

import { E_APP_CONTROL_MODE, type TAppControl } from "./types";
import {
  APP_CONTROL_COLLECTION,
  APP_CONTROL_MAX_TIMEOUT_DELAY_MS
} from "./constants";

class AppControlStore {
  /**
   * Resolved state UI components read from. Everything else below is
   * internal bookkeeping used to produce these.
   */
  isFirebaseMaintenanceActive = observable.box<boolean>(false);

  isFirebaseVersionUpdateActive = observable.box<boolean>(false);

  warningTitle = observable.box<string>("");

  warningMessage = observable.box<string>("");

  appControlError = observable.box<string>("");

  /**
   * Latest validated document, kept so the boundary timer can
   * re-resolve state without another Firestore read.
   */
  latestDocument = observable.box<TAppControl | null>(null);

  unsubscribeSnapshot = observable.box<Unsubscribe | null>(null);

  boundaryTimer = observable.box<number | null>(null);

  isLifecycleActive = observable.box<boolean>(false);

  /**
   * warningId last offered to the user this session, so repeated
   * snapshots/timer ticks for the same still-unacknowledged warning
   * don't reopen the popup on top of itself.
   */
  lastAutoOpenedWarningId = observable.box<number | null>(null);

  /**
   * warningId the currently-open popup (if any) is acknowledging.
   */
  pendingAcknowledgeWarningId = observable.box<number | null>(null);

  start = (): void => {
    if (this.unsubscribeSnapshot.get()) {
      return;
    }

    runInAction(() => {
      this.isLifecycleActive.set(true);
    });

    const documentId: string = reduce(
      () => configuration.appControlDocumentId,
      ""
    );

    if (!isDefinedString(documentId)) {
      this.handleAppControlFailure(
        i18n.get("APP_CONTROL_DOCUMENT_ID_NOT_CONFIGURED")
      );

      return;
    }

    try {
      const appControlDocRef = doc(
        getFirestore(firebaseApp),
        APP_CONTROL_COLLECTION,
        documentId
      );

      const unsubscribe = onSnapshot(
        appControlDocRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            this.handleAppControlFailure(
              i18n.getWithExtraData(
                "APP_CONTROL_DOCUMENT_NOT_FOUND",
                documentId
              )
            );

            return;
          }

          const mapped = this.mapFirestoreDataToAppControl(snapshot.data());

          if (!mapped) {
            this.handleAppControlFailure(
              i18n.getWithExtraData(
                "APP_CONTROL_DOCUMENT_MALFORMED",
                documentId
              )
            );

            return;
          }

          runInAction(() => {
            this.appControlError.set("");
          });

          this.applyDocument(mapped);
        },
        (error: FirestoreError) => {
          this.handleAppControlFailure(
            i18n.getWithExtraData("APP_CONTROL_LISTENER_ERROR", error.message)
          );
        }
      );

      runInAction(() => {
        this.unsubscribeSnapshot.set(unsubscribe);
      });
    } catch (error) {
      const message: string = reduce(
        () => (<Error>error).message,
        i18n.get("APP_CONTROL_UNKNOWN_INITIALIZATION_ERROR")
      );

      this.handleAppControlFailure(message);
    }
  };

  stop = (): void => {
    runInAction(() => {
      this.isLifecycleActive.set(false);
    });

    this.clearBoundaryTimer();

    const unsubscribe = this.unsubscribeSnapshot.get();

    if (unsubscribe) {
      unsubscribe();

      runInAction(() => {
        this.unsubscribeSnapshot.set(null);
      });
    }

    runInAction(() => {
      this.latestDocument.set(null);

      this.lastAutoOpenedWarningId.set(null);
    });
  };

  /**
   * Called by the warning popup's OK action. Persists the acknowledged
   * warningId (so it never reappears for this browser) and closes the
   * popup through the existing confirmation-popup flow.
   */
  acknowledgeWarning = (): void => {
    const warningId = this.pendingAcknowledgeWarningId.get();

    if (warningId !== null) {
      getCoreStorageStore(E_STORAGE_TYPE.LOCAL).set<number>(
        E_LOCAL_STORAGE_KEY.APP_CONTROL_LAST_SEEN_WARNING_ID,
        warningId
      );
    }

    getCorePopUpStore().closeConfirmationPopUps();

    runInAction(() => {
      this.pendingAcknowledgeWarningId.set(null);
    });
  };

  /**
   * Resolves the effective state for the latest document at `Date.now()`,
   * updates observables, evaluates the warning popup trigger, then
   * schedules the timer for the next relevant time boundary.
   */
  applyDocument = (document: TAppControl): void => {
    runInAction(() => {
      this.latestDocument.set(document);
    });

    this.recomputeAndSchedule();
  };

  recomputeAndSchedule = (): void => {
    const document = this.latestDocument.get();

    if (!document) {
      return;
    }

    const now = Date.now();

    const isMaintenanceActive =
      document.mode === E_APP_CONTROL_MODE.MAINTENANCE &&
      this.isWithinAppControlWindow({
        startsAtUnixMs: document.maintenanceStartsAtUnixMs,
        endsAtUnixMs: document.maintenanceEndsAtUnixMs,
        now
      });

    const isVersionUpdateActive =
      document.mode === E_APP_CONTROL_MODE.VERSION_UPDATE &&
      this.isWithinAppControlWindow({
        startsAtUnixMs: document.versionUpdateStartsAtUnixMs,
        endsAtUnixMs: document.versionUpdateEndsAtUnixMs,
        now
      });

    const isWarningWindowActive =
      document.mode === E_APP_CONTROL_MODE.NORMAL &&
      document.showWarningMessage &&
      this.isWithinAppControlWindow({
        startsAtUnixMs: document.warningMessageStartsAtUnixMs,
        endsAtUnixMs: document.warningMessageEndsAtUnixMs,
        now
      });

    runInAction(() => {
      this.isFirebaseMaintenanceActive.set(isMaintenanceActive);

      this.isFirebaseVersionUpdateActive.set(isVersionUpdateActive);
    });

    this.evaluateWarningPopup(document, isWarningWindowActive);

    this.scheduleNextBoundaryTimer(document);
  };

  evaluateWarningPopup = (
    document: TAppControl,
    isWarningWindowActive: boolean
  ): void => {
    if (!isWarningWindowActive) {
      runInAction(() => {
        this.lastAutoOpenedWarningId.set(null);
      });

      this.autoCloseExpiredWarningPopup(document);

      return;
    }

    // Logged-out users never see the popup anyway (Et3ConfirmationPopUp
    // only mounts once logged in), but without this check a warning
    // window passing entirely while logged out would still mark
    // lastAutoOpenedWarningId here - so it would never show once the
    // user does log in.
    if (!isPositiveNumber(getCorePersonalStore().user.get().id)) {
      return;
    }

    const lastSeenWarningId = getCoreStorageStore(
      E_STORAGE_TYPE.LOCAL
    ).get<number>(E_LOCAL_STORAGE_KEY.APP_CONTROL_LAST_SEEN_WARNING_ID);

    if (document.warningId === lastSeenWarningId) {
      return;
    }

    if (this.lastAutoOpenedWarningId.get() === document.warningId) {
      return;
    }

    runInAction(() => {
      this.warningTitle.set(document.warningTitle);

      this.warningMessage.set(document.warningMessage);

      this.pendingAcknowledgeWarningId.set(document.warningId);

      this.lastAutoOpenedWarningId.set(document.warningId);
    });

    getCorePopUpStore().openConfirmationPopUps("APP_CONTROL_WARNING_POPUP");
  };

  /**
   * Closes an already-open App Control warning popup once its window
   * has ended - but only the exact expiry case (mode still NORMAL,
   * bounded end, now past it), never on a mode change or a start not
   * yet reached, and never someone else's popup. Does not acknowledge:
   * `lastSeenWarningId` is untouched, so the same warningId can still
   * reopen later if Firebase re-extends its window.
   */
  autoCloseExpiredWarningPopup = (document: TAppControl): void => {
    const hasWarningExpired =
      document.mode === E_APP_CONTROL_MODE.NORMAL &&
      document.showWarningMessage &&
      document.warningMessageEndsAtUnixMs !== 0 &&
      Date.now() >= document.warningMessageEndsAtUnixMs;

    if (!hasWarningExpired) {
      return;
    }

    if (
      getCorePopUpStore().confirmationPopUpType.get() !==
      "APP_CONTROL_WARNING_POPUP"
    ) {
      return;
    }

    getCorePopUpStore().closeConfirmationPopUps();

    runInAction(() => {
      this.pendingAcknowledgeWarningId.set(null);
    });
  };

  /**
   * The next timestamp (strictly after `now`) at which the resolved
   * state for the document's current mode could change, or null if
   * there is nothing left to wait for (already unbounded/inactive).
   */
  getNextBoundary = (document: TAppControl, now: number): number | null => {
    let candidates: number[] = [];

    switch (document.mode) {
      case E_APP_CONTROL_MODE.MAINTENANCE:
        candidates = [
          document.maintenanceStartsAtUnixMs,
          document.maintenanceEndsAtUnixMs
        ];
        break;

      case E_APP_CONTROL_MODE.VERSION_UPDATE:
        candidates = [
          document.versionUpdateStartsAtUnixMs,
          document.versionUpdateEndsAtUnixMs
        ];
        break;

      case E_APP_CONTROL_MODE.NORMAL:
        if (document.showWarningMessage) {
          candidates = [
            document.warningMessageStartsAtUnixMs,
            document.warningMessageEndsAtUnixMs
          ];
        }
        break;
    }

    const futureCandidates = candidates.filter(
      (timestamp) => timestamp !== 0 && timestamp > now
    );

    if (futureCandidates.length === 0) {
      return null;
    }

    return Math.min(...futureCandidates);
  };

  clearBoundaryTimer = (): void => {
    const timer = this.boundaryTimer.get();

    if (timer !== null) {
      window.clearTimeout(timer);

      runInAction(() => {
        this.boundaryTimer.set(null);
      });
    }
  };

  /**
   * Schedules a single re-resolution at the next relevant boundary.
   * Delays longer than the safe setTimeout range are capped and
   * rescheduled in steps rather than relying on one oversized timeout.
   */
  scheduleNextBoundaryTimer = (document: TAppControl): void => {
    this.clearBoundaryTimer();

    if (!this.isLifecycleActive.get()) {
      return;
    }

    const now = Date.now();

    const nextBoundary = this.getNextBoundary(document, now);

    if (nextBoundary === null) {
      return;
    }

    const delay = Math.min(
      nextBoundary - now,
      APP_CONTROL_MAX_TIMEOUT_DELAY_MS
    );

    const timer = window.setTimeout(() => {
      runInAction(() => {
        this.boundaryTimer.set(null);
      });

      this.recomputeAndSchedule();
    }, delay);

    runInAction(() => {
      this.boundaryTimer.set(timer);
    });
  };

  /**
   * Fail-open: any Firestore/document problem resolves to no
   * maintenance, no version update, no warning. Never throws.
   */
  handleAppControlFailure = (message: string): void => {
    console.warn(message);

    this.clearBoundaryTimer();

    runInAction(() => {
      this.latestDocument.set(null);

      this.lastAutoOpenedWarningId.set(null);

      this.isFirebaseMaintenanceActive.set(false);

      this.isFirebaseVersionUpdateActive.set(false);

      this.appControlError.set(message);
    });
  };

  /**
   * Whether `now` falls within [startsAtUnixMs, endsAtUnixMs).
   *
   * A `0` on either side means "unbounded" on that side: 0/0 is active
   * immediately and indefinitely, start/0 is active from `start` onward,
   * 0/end is active immediately until `end`, start/end is a normal range.
   *
   * The end bound is exclusive (`now < endsAtUnixMs`) rather than `<=`.
   * getNextBoundary() only schedules timers for timestamps strictly in
   * the future (`timestamp > now`); if `end` were inclusive, a timer
   * firing at the exact moment `now === end` would still see the state
   * as active and, since `end` is no longer a future timestamp, would
   * never schedule another check - leaving it active indefinitely.
   */
  isWithinAppControlWindow = ({
    startsAtUnixMs,
    endsAtUnixMs,
    now
  }: {
    startsAtUnixMs: number;
    endsAtUnixMs: number;
    now: number;
  }): boolean => {
    const startsOk = startsAtUnixMs === 0 || now >= startsAtUnixMs;
    const endsOk = endsAtUnixMs === 0 || now < endsAtUnixMs;

    return startsOk && endsOk;
  };

  isValidAppControlMode = (value: any): value is E_APP_CONTROL_MODE =>
    isDefinedString(value) &&
    Object.values(E_APP_CONTROL_MODE).includes(<E_APP_CONTROL_MODE>value);

  /**
   * Validates and maps a raw Firestore document into TAppControl.
   * Returns null for any missing/malformed field so the caller can fail
   * open instead of trusting untyped Firestore data.
   */
  mapFirestoreDataToAppControl = (data: DocumentData): TAppControl | null => {
    if (!isDefinedObject(data)) {
      return null;
    }

    const raw = <DocumentData>data;

    if (
      !this.isValidAppControlMode(raw.mode) ||
      !isPositiveNumber(raw.maintenanceStartsAtUnixMs, true) ||
      !isPositiveNumber(raw.maintenanceEndsAtUnixMs, true) ||
      !isPositiveNumber(raw.versionUpdateStartsAtUnixMs, true) ||
      !isPositiveNumber(raw.versionUpdateEndsAtUnixMs, true) ||
      !isDefinedBoolean(raw.showWarningMessage) ||
      !isPositiveNumber(raw.warningId, true) ||
      !isString(raw.warningTitle) ||
      !isString(raw.warningMessage) ||
      !isPositiveNumber(raw.warningMessageStartsAtUnixMs, true) ||
      !isPositiveNumber(raw.warningMessageEndsAtUnixMs, true)
    ) {
      return null;
    }

    return {
      mode: <E_APP_CONTROL_MODE>raw.mode,
      maintenanceStartsAtUnixMs: <number>raw.maintenanceStartsAtUnixMs,
      maintenanceEndsAtUnixMs: <number>raw.maintenanceEndsAtUnixMs,
      versionUpdateStartsAtUnixMs: <number>raw.versionUpdateStartsAtUnixMs,
      versionUpdateEndsAtUnixMs: <number>raw.versionUpdateEndsAtUnixMs,
      showWarningMessage: <boolean>raw.showWarningMessage,
      warningId: <number>raw.warningId,
      warningTitle: <string>raw.warningTitle,
      warningMessage: <string>raw.warningMessage,
      warningMessageStartsAtUnixMs: <number>raw.warningMessageStartsAtUnixMs,
      warningMessageEndsAtUnixMs: <number>raw.warningMessageEndsAtUnixMs
    };
  };
}

const getAppControlStore = memoize(
  () => new AppControlStore(),
  () => 1
);

export default getAppControlStore;
